import { HttpClientType } from 'api/axios';
import { usePatchInstrumentApi } from 'api/mypage/instrument';
import { usePartnerPatchInstrumentApi } from 'api/partner/instrument';
import { useFetchExColumnsApi } from 'api/shared/ex_columns';
import { SelectField } from 'components/shared/form/input';
import { Flex } from 'components/shared/flex';
import { Hint } from 'components/shared/hint';
import Modal, { ModalState } from 'components/shared/modal';
import ThemedButton from 'components/shared/themed_button';
import { GlobalStateContext } from 'contexts/global_state_context';
import { ExColumn } from 'entities/ex_column';
import { Instrument, InstrumentForm } from 'entities/instrument';
import { InstrumentProofreadExAttributeValueForm } from 'entities/instrument_proofread_ex_attribute_value';
import { useForm } from 'rac';
import React, { useContext, useEffect, useMemo } from 'react';
import { useEffectSkipFirst } from 'utils/hooks';

type Props = {
  instrument: Instrument;
  modalState: ModalState;
  onComplete: () => void;
  clientType: HttpClientType;
};

const InstrumentProofreadExAttributeDefaultsFormDialog: React.FC<Props> = (props) => {
  const globalState = useContext(GlobalStateContext);
  const mypagePatchApi = usePatchInstrumentApi();
  const partnerPatchApi = usePartnerPatchInstrumentApi();
  const patchApi = props.clientType === 'dealer_user' ? partnerPatchApi : mypagePatchApi;
  const exColumnsApi = useFetchExColumnsApi(props.clientType);

  const form = useForm<InstrumentForm>({}, 'instrument');

  useEffect(() => {
    exColumnsApi.execute();
  }, []);

  const proofreadDropdownExColumns: ExColumn[] = useMemo(
    () =>
      (exColumnsApi.response.exColumns || []).filter(
        (col) => col.extensionClass === 'ProofreadEvent' && col.inputType === 'dropdown'
      ),
    [exColumnsApi.response.exColumns]
  );

  useEffect(() => {
    if (props.modalState.open && props.instrument.id && proofreadDropdownExColumns.length > 0) {
      const defaults = props.instrument.proofreadExAttributeDefaults || [];
      const defaultsByExColumnId = new Map(defaults.map((d) => [d.exColumnId, d]));
      // ex_columnsの順序に合わせてフォームデータを構築
      const attrs: InstrumentProofreadExAttributeValueForm[] = proofreadDropdownExColumns.map((col) => {
        const saved = defaultsByExColumnId.get(col.id);
        return {
          id: saved?.id ?? '',
          exColumnId: col.id,
          value: saved?.value || '',
        };
      });
      form.set({
        id: props.instrument.id,
        instrumentProofreadExAttributeValuesAttributes: attrs,
      } as InstrumentForm);
    }
  }, [props.modalState.open, props.instrument, proofreadDropdownExColumns]);

  useEffect(() => {
    globalState.setLoading(patchApi.loading);
  }, [patchApi.loading]);

  useEffectSkipFirst(() => {
    if (patchApi.response.instrument?.id) {
      props.onComplete();
    }
  }, [patchApi.response]);

  const handleSubmit = () => {
    patchApi.execute(form.object);
  };

  return (
    <Modal
      modalId="proofread-ex-attribute-defaults-dialog"
      modalState={props.modalState}
      title={
        <Flex alignItems="center">
          校正管理情報
          <Hint
            title={
              <>
                校正計画作成時に機器を絞り込むための設定です。
                <br />
                ドロップダウン型の校正管理情報のみが対象です。
              </>
            }
            tooltipStyle={{ marginLeft: 8 }}
          />
        </Flex>
      }
      size="sm"
      footer={
        <>
          <ThemedButton color="secondary" onClick={() => props.modalState.setOpen(false)}>
            閉じる
          </ThemedButton>
          <ThemedButton color="success" onClick={handleSubmit}>
            登録
          </ThemedButton>
        </>
      }
    >
      {proofreadDropdownExColumns.length === 0 ? (
        <>校正管理情報が設定されていません</>
      ) : (
        <>
          {proofreadDropdownExColumns.map((exColumn, idx) => (
            <SelectField
              key={exColumn.id}
              form={form}
              labelId={`proofread-default-${exColumn.id}`}
              attr={['instrumentProofreadExAttributeValuesAttributes', idx, 'value']}
              label={exColumn.name || ''}
              data={(exColumn.options || []).map((opt) => ({ value: opt, label: opt }))}
              includeBlank
              blankText="未設定"
              apiError={patchApi.apiError}
            />
          ))}
        </>
      )}
    </Modal>
  );
};

export default InstrumentProofreadExAttributeDefaultsFormDialog;
